import { Box, Tooltip, Typography } from '@material-ui/core';
import FavoriteIcon from '@mui/icons-material/Favorite';
import HomeIcon from '@mui/icons-material/Home';
import { useQueryClient } from '@tanstack/react-query';
import React, { useEffect } from 'react';
import { toast } from 'react-toastify';
import { useTranslation } from 'react-i18next';
import { toggleFavouriteDynamicPanel } from '../../../../services/dynamicPanels';
import { useDynamicViewState } from '../../context/dynamic-view-context';
import { DeleteDashboard } from './DeleteDashboard';

/**
 * @fileoverview Cabecera de tarjeta de selección de dashboard.
 * @module TemplateCardSelect
 */

/**
 * Componente TemplateCardSelect.
 *
 * Muestra la barra de título y acciones para cada tarjeta de dashboard en la
 * lista de "Mis Dashboards".
 *
 * Funcionalidades:
 * - Mostrar el nombre del dashboard.
 * - Alternar el estado de favorito (marcar/desmarcar).
 * - Mostrar el botón de eliminación.
 *
 * @param {Object} props - Propiedades del componente.
 * @param {Object} props.panel - Objeto con la información del dashboard.
 * @param {number} props.panel.panel_dinamico_usuario_id - ID del panel.
 * @param {string} props.panel.nombre - Nombre del panel.
 * @param {boolean} props.panel.es_favorito - Estado inicial de favorito.
 * @param {boolean} props.panel.mostrar_en_home - Estado inicial de home.
 * @param {Function} props.onDeleteSuccess - Callback al eliminar el panel.
 *
 * @returns {React.ReactElement} Barra de cabecera de la tarjeta de dashboard.
 */
export const TemplateCardSelect = React.memo(function TemplateCardSelect({
  panel,
  onDeleteSuccess,
  handleHome
}) {
  const { t } = useTranslation();
  const [favourite, setFavourite] = React.useState(panel.es_favorito ?? false);
  const [isHome, setIsHome] = React.useState(panel.mostrar_en_home ?? false);
  const state = useDynamicViewState();
  const queryClient = useQueryClient();

  useEffect(() => {
    setIsHome(panel.mostrar_en_home ?? false);
  }, [panel.mostrar_en_home]);

  async function handleSetFavourite() {
    try {
      await toggleFavouriteDynamicPanel({
        panelId: panel.panel_dinamico_usuario_id,
        isFavourite: !favourite,
      });
      setFavourite(!favourite);
      if (!state.activeLayoutId) {
        queryClient.invalidateQueries({
          queryKey: ['dynamicPanelsUser'],
        });
      }
    } catch (error) {
      toast.error(t('dynamicPanel.templateCardSelect.updateError'));
    }
  }
  async function handleSetHome() {
      const res = await handleHome(panel.panel_dinamico_usuario_id, !isHome);
      console.log(res)
      if(res){
        queryClient.invalidateQueries({ queryKey: ['dynamicPanelsUser'] });
        queryClient.invalidateQueries({ queryKey: ['dynamicPanelHomeUser'] });
      } 
  }

  return (
    <Box
      style={{
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
      }}
    >
      <Box
        style={{
          width: '50%',
          display: 'flex',
          gap: '10px',
        }}
      >
        <Tooltip title={t('dynamicPanel.templateCardSelect.favoriteTooltip')}>
          <FavoriteIcon
            style={{ cursor: 'pointer', color: favourite ? 'red' : '#858585' }}
            onClick={handleSetFavourite}
          />
        </Tooltip>
        <Tooltip title={t('dynamicPanel.templateCardSelect.homeTooltip')}>
          <HomeIcon
            style={{ 
              cursor: 'pointer', 
              color: isHome ? '#3f51b5' : '#858585' 
            }}
            onClick={handleSetHome}
          />
        </Tooltip>
        <Typography
          variant="h5"
          style={{ alignSelf: 'center', marginBottom: 0 }}
        >
          {panel.nombre || t('dynamicPanel.templateCardSelect.customPanel')}
        </Typography>
      </Box>
      <Box
        style={{
          width: '50%',
          display: 'flex',
          justifyContent: 'flex-end',
        }}
      >
        <DeleteDashboard
          panelId={panel.panel_dinamico_usuario_id}
          onDeleteSuccess={onDeleteSuccess}
        />
      </Box>
    </Box>
  );
});
